import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { store } from "../store";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const initialSettings = {
  direction: "ltr",
  language: "nb",
  pinSidebar: true,
  theme: "dark",
};

const localeTranslationFromRails = {
  en: "en",
  nb: "nb",
  nn: "nb", // Rails Nynorsk locale → same Bokmål UI bundle until a dedicated nn UI exists
  sv: "se",
  no: "nb",
};

const localeCurrency = {
  en: "USD",
  nb: "NOK",
  nn: "NOK",
  no: "NOK",
  sv: "SEK",
};

// Utility function to detect user locale from various sources
const detectUserLocale = (user) => {
  if (!user) return null;

  // Check direct locale field first
  if (user.locale) {
    return user.locale;
  }

  // Check settings object
  if (user.settings?.locale) {
    return user.settings.locale;
  }

  // Check if there's a nested settings structure
  if (user.data?.settings?.locale) {
    return user.data.settings.locale;
  }
  return null;
};

// Utility function to map locale to i18n language code
const mapLocaleToLanguage = (locale) => {
  if (!locale) return "en";

  const mappedLanguage = localeTranslationFromRails[locale];

  return mappedLanguage || "en";
};

export const restoreSettings = async () => {
  const user = await store.getState().session.currentUser;

  let settings = null;

  try {
    const storedData = window.localStorage.getItem("settings");

    if (storedData) {
      settings = JSON.parse(storedData);
    } else {
      // Get user locale from API response using utility function
      const userLocale = detectUserLocale(user);
      const mappedLanguage = mapLocaleToLanguage(userLocale);

      settings = {
        direction: "ltr",
        language: mappedLanguage,
        pinSidebar: true,
        theme: window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light",
      };
    }
  } catch (err) {
    console.error("Error restoring settings:", err);
    // If stored data is not a stringified JSON this will fail,
    // that's why we catch the error
    settings = {
      direction: "ltr",
      language: "en",
      pinSidebar: true,
      theme: window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light",
    };
  }

  return settings;
};

export const storeSettings = (settings) => {
  window.localStorage.setItem("settings", JSON.stringify(settings));
};

export const SettingsContext = createContext({
  settings: initialSettings,
  saveSettings: () => {},
});

export const SettingsProvider = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState(initialSettings);
  const [isInitialized, setIsInitialized] = useState(false);
  const { i18n } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);

  useEffect(() => {
    const initializeSettings = async () => {
      try {
        const restoredSettings = await restoreSettings();
        if (restoredSettings) {
          setSettings(restoredSettings);

          // Change language immediately after settings are restored
          if (restoredSettings.language) {
            await i18n.changeLanguage(restoredSettings.language);
          }
        }
      } catch (err) {
        console.error("Could not restore settings:", err);
        // Fallback to default settings
        setSettings(initialSettings);
        await i18n.changeLanguage(initialSettings.language);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeSettings();
  }, [i18n]);

  // Re-initialize settings when user data becomes available
  useEffect(() => {
    if (currentUser && isInitialized) {
      const userLocale = detectUserLocale(currentUser);
      const mappedLanguage = mapLocaleToLanguage(userLocale);

      // Only update if the language is different from current
      if (mappedLanguage !== settings.language) {
        const updatedSettings = {
          ...settings,
          language: mappedLanguage,
        };

        setSettings(updatedSettings);
        storeSettings(updatedSettings);

        i18n.changeLanguage(mappedLanguage).catch((err) => {
          console.error("Could not change language after user update:", err);
        });
      }
    }
  }, [currentUser, isInitialized, settings.language, i18n]);

  const saveSettings = async (updatedSettings) => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);

    if (
      updatedSettings.language &&
      updatedSettings.language !== settings.language
    ) {
      try {
        await i18n.changeLanguage(updatedSettings.language);
      } catch (err) {
        console.error("Could not change language:", err);
      }
    }
  };

  const priceFormatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  if (!isInitialized) {
    return null;
  }

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
        priceFormatter,
        // Utility functions for debugging and locale management
        getCurrentLanguage: () => settings.language,
        getCurrentLocale: () => {
          const user = store.getState().session.currentUser;
          return detectUserLocale(user);
        },
        isLanguageLoaded: () => isInitialized,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const SettingsConsumer = SettingsContext.Consumer;

export const useSettings = () => useContext(SettingsContext);
